import React, { useState, useEffect, useMemo, useRef } from 'react';
import TagCard from './TagCard';
import { TagGridSkeleton } from './common';
import { fetchHotTags, fetchRegions, mergeAndSortRegions, DEFAULT_UI_REGIONS } from '../services/api';
import { useDashboardFilters } from '../contexts/FilterContext';
import './TrendsDashboard.css';

/**
 * Trends Dashboard Component
 * Main dashboard showing hot trending tags
 * 
 * @param {Object} props
 * @param {Function} props.onTagClick - Callback when tag is clicked (receives tagId)
 * @param {Function} props.onMediaViewClick - Callback to navigate to media view
 */
function TrendsDashboard({ onTagClick, onMediaViewClick }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regions, setRegions] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const isLoadingRef = useRef(false);
  
  // Filters state — persisted in sessionStorage via FilterContext
  // This ensures filters are remembered when navigating back to this view
  const { filters, setFilter } = useDashboardFilters();

  // Load initial data (regions: global, sg, us, uk; merge with API when available)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const regionsData = await fetchRegions();
        setRegions(mergeAndSortRegions(regionsData));
      } catch (err) {
        console.error('Failed to load initial data:', err);
        setRegions(DEFAULT_UI_REGIONS);
      }
    };
    loadInitialData();
  }, []);

  // Extract filter values to use in useEffect - this prevents infinite loops
  // Use individual filter values with defaults to ensure stable dependencies
  const regionCode = filters?.region_code ?? 'GLOBAL';
  const platform = filters?.platform ?? 'all';
  const mediaType = filters?.media_type ?? 'all';
  const timeWindow = filters?.time_window ?? '24h';
  const sortBy = filters?.sort_by ?? 'score';
  const limit = filters?.limit ?? 30;

  const filterParams = useMemo(() => ({
    region_code: regionCode,
    platform: platform,
    media_type: mediaType,
    time_window: timeWindow,
    sort_by: sortBy,
    limit: limit
  }), [regionCode, platform, mediaType, timeWindow, sortBy, limit]);

  // Load tags when filters change or reload is triggered
  useEffect(() => {
    // Prevent concurrent requests
    if (isLoadingRef.current) {
      return;
    }

    let cancelled = false;
    const abortController = new AbortController();
    isLoadingRef.current = true;
    
    const loadTags = async () => {
      setLoading(true);
      setError(null);
      // Clear tags immediately when region changes to prevent showing stale data
      setTags([]);
      
      try {
        const response = await fetchHotTags(filterParams, { signal: abortController.signal });
        // Only update state if this request hasn't been cancelled
        if (!cancelled) {
          setTags(response.tags || []);
        }
      } catch (err) {
        // Ignore abort errors (they're expected when cancelling)
        if (err.name === 'AbortError') {
          return;
        }
        // Only show error if request wasn't cancelled
        if (!cancelled) {
          setError('Failed to load trending tags. Please try again.');
          console.error('Failed to load tags:', err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          isLoadingRef.current = false;
        }
      }
    };
    
    loadTags();
    
    // Cleanup: cancel request if component unmounts or filters change
    return () => {
      cancelled = true;
      isLoadingRef.current = false;
      abortController.abort();
    };
  }, [filterParams, reloadKey]);

  const handleFilterChange = (key, value) => {
    setFilter(key, value);
  };

  const handleTagClick = (tag) => {
    if (onTagClick) {
      // Pass current region context when navigating to tag detail
      onTagClick(tag.tag_id, filters.region_code);
    }
  };

  const handleRetry = () => {
    setReloadKey(prev => prev + 1);
  };

  return (
    <div className="trends-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <h1>🏷️ Trending Tags</h1>
          <p className="subtitle">Tags aggregated across all platforms • {filters.region_code === 'GLOBAL' ? 'Worldwide' : regions.find(r => r.region_code === filters.region_code)?.region_name || filters.region_code}</p>
        </div>
      </header>

      {/* Region Pills */}
      <div className="region-pills">
        {regions.slice(0, 4).map(region => (
          <button
            key={region.region_code}
            className={`region-pill ${filters.region_code === region.region_code ? 'active' : ''}`}
            onClick={() => handleFilterChange('region_code', region.region_code)}
          >
            {region.region_code === 'GLOBAL' && '🌐'}
            {region.region_code === 'SG' && '🇸🇬'}
            {region.region_code === 'US' && '🇺🇸'}
            {region.region_code === 'GB' && '🇬🇧'}
            {' '}{region.region_name}
          </button>
        ))}
      </div>

      {/* Filters Row */}
      <div className="filters-row">
        <div className="filter-group">
          <label>Time:</label>
          <select 
            value={filters.time_window}
            onChange={(e) => handleFilterChange('time_window', e.target.value)}
          >
            <option value="24h">Past 24 hours</option>
            <option value="7d">Past 7 days</option>
            <option value="30d">Past 30 days</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Top:</label>
          <select 
            value={filters.limit || 30}
            onChange={(e) => handleFilterChange('limit', parseInt(e.target.value, 10))}
          >
            <option value={10}>Top 10</option>
            <option value={30}>Top 30</option>
            <option value={50}>Top 50</option>
          </select>
        </div>

        <div className="results-count">
          {!loading && !error && `${tags.length} tags`}
        </div>
      </div>

      {/* Loading State - Skeleton */}
      {loading && <TagGridSkeleton count={6} />}

      {/* Error State */}
      {error && (
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <p>{error}</p>
          <button className="retry-btn" onClick={handleRetry}>
            Try Again
          </button>
        </div>
      )}

      {/* Tags Grid */}
      {!loading && !error && tags.length > 0 && (
        <div className="tags-grid">
          {tags.map((tag) => (
            <TagCard 
              key={tag.tag_id}
              tag={tag}
              onClick={() => handleTagClick(tag)}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && tags.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h3>No trending tags found</h3>
          <p>Try selecting a different region or check back later.</p>
        </div>
      )}
    </div>
  );
}

export default TrendsDashboard;
